import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import { useQueries } from "react-query";
import {
  transformArrayField,
  transformTextField,
  generateTextFieldLookup,
  generateArrayFieldLookup,
} from "../../../utils/dataTransforms";
import { useTableFilterLookups } from "../../../hooks/useTableFilterLookups";
import MaterialTable from "material-table";
import {
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Typography,
  Button,
  Box,
  Link,
  Divider as MuiDivider,
  Breadcrumbs as MuiBreadcrumbs,
} from "@material-ui/core";
import { useAuth0 } from "@auth0/auth0-react";
import PlusIcon from "@material-ui/icons/Add";
import MinusIcon from "@material-ui/icons/Remove";
import { Alert } from "@material-ui/lab";
import { Helmet } from "react-helmet-async";
import { NavLink } from "react-router-dom";
import { spacing } from "@material-ui/system";
import styled from "styled-components/macro";

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const BASE_URL = process.env.REACT_APP_ENDPOINT;
const QUERY_CONFIGS = [
  {
    queryKey: "ui-cq-pumpage-calendar-crosstab-bypermitgroup",
    url: `${BASE_URL}/api/ui-cq-pumpage-calendar-crosstab-bypermitgroup`,
  },
  {
    queryKey: "ui-cq-pumpage-fiscal-crosstab-bypermitgroup",
    url: `${BASE_URL}/api/ui-cq-pumpage-fiscal-crosstab-bypermitgroup`,
  },
];

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const FISCAL_NAMES = [
  "September",
  "October",
  "November",
  "December",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
];

const tableOptions = {
  emptyRowsWhenPaging: false,
  exportAllData: true,
  columnsButton: true,
  exportButton: { csv: true },
  pageSize: 30,
  pageSizeOptions: [5, 10, 30, 60],
  padding: "dense",
  searchFieldAlignment: "left",
  showTitle: false,
  maxBodyHeight: "600px",
  filtering: true,
  headerStyle: {
    whiteSpace: "nowrap",
    fontWeight: 600,
  },
  cellStyle: {
    whiteSpace: "nowrap",
  },
};

function useDebounce(callback, delay) {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  return useCallback(
    (...args) => {
      if (callbackRef.current.timeoutId) {
        clearTimeout(callbackRef.current.timeoutId);
      }
      callbackRef.current.timeoutId = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    },
    [delay]
  );
}

function getOrderedMonths(yearOption) {
  return yearOption === "calendar" ? MONTH_NAMES : FISCAL_NAMES;
}

function formatNumber(value) {
  return value != null ? Number(value).toLocaleString() : "";
}

const MultiSelect = ({ label, options, selected, onChange, getLabel }) => {
  const isAllSelected =
    options.length > 0 && selected.length === options.length;

  const handleToggleAll = () => {
    if (isAllSelected) {
      onChange([]);
    } else {
      onChange([...options]);
    }
  };

  const handleChange = (event) => {
    const value = event.target.value;
    if (value[value.length - 1] === "selectAll") {
      handleToggleAll();
    } else {
      onChange(value);
    }
  };

  return (
    <FormControl variant="outlined" fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        multiple
        value={selected}
        onChange={handleChange}
        renderValue={(selected) => selected.map(getLabel).join(", ")}
      >
        {options.length > 0 && (
          <MenuItem
            value="selectAll"
            onClick={handleToggleAll}
            style={{
              fontWeight: 600,
              padding: "16px",
              justifyContent: "center",
              color: "#17A5B9",
              gap: "6px",
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                border: "2px solid #17A5B9",
                color: "#17A5B9",
              }}
            >
              {isAllSelected ? <MinusIcon /> : <PlusIcon />}
            </span>
            {isAllSelected ? "Deselect All" : "Select All"}
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem key={getLabel(option)} value={option}>
            <Checkbox
              checked={selected.some(
                (item) => getLabel(item) === getLabel(option)
              )}
            />
            <ListItemText primary={getLabel(option)} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default function PumpageByPermitGroup() {
  const { getAccessTokenSilently } = useAuth0();

  const results = useQueries(
    QUERY_CONFIGS.map((config) => ({
      queryKey: config.queryKey,
      queryFn: async () => {
        const token = await getAccessTokenSilently();
        const headers = { Authorization: `Bearer ${token}` };
        const { data } = await axios.get(config.url, { headers });
        return data;
      },
      staleTime: Infinity,
      cacheTime: Infinity,
    }))
  );

  const [{ data: calendarData = [] }, { data: fiscalData = [] }] = results;

  const hasError = results.some((result) => result.error);
  const isLoading = results.some((result) => result.isLoading);

  const [filters, setFilters] = useState({
    reportYearOption: "calendar",
    startYear: 2008,
    endYear: new Date().getFullYear(),

    aquiferGroupsList: [],
    mgmtZoneList: [],
    permitTypesList: [],
    permitUseList: [],
    permitStatusList: [],
    permitTierList: [],

    linkedWells: "",
    permits: "",
    permitNames: "",
  });

  const [linkedWellsDraft, setLinkedWellsDraft] = useState("");
  const [permitsDraft, setPermitsDraft] = useState("");
  const [permitNamesDraft, setPermitNamesDraft] = useState("");

  const [filtersInitialized, setFiltersInitialized] = useState(false);

  const combinedData = useMemo(() => {
    return [...calendarData, ...fiscalData];
  }, [calendarData, fiscalData]);

  const lookupValues = useMemo(() => {
    return {
      aquiferGroupsList: generateArrayFieldLookup(
        combinedData,
        "aquifer_groups_array"
      ),
      mgmtZoneList: generateArrayFieldLookup(combinedData, "management_zones"),
      permitTypesList: generateArrayFieldLookup(
        combinedData,
        "permit_types_array"
      ),
      permitUseList: generateArrayFieldLookup(combinedData, "permit_use_array"),
      permitStatusList: generateArrayFieldLookup(
        combinedData,
        "permit_status_array"
      ),
      permitTierList: generateTextFieldLookup(combinedData, "permit_tier_name"),
    };
  }, [combinedData]);

  useEffect(() => {
    if (
      !filtersInitialized &&
      lookupValues.aquiferGroupsList.length > 0 &&
      combinedData.length
    ) {
      setFilters((prev) => ({
        ...prev,
        aquiferGroupsList: lookupValues.aquiferGroupsList,
        mgmtZoneList: lookupValues.mgmtZoneList,
        permitTypesList: lookupValues.permitTypesList,
        permitUseList: lookupValues.permitUseList,
        permitStatusList: lookupValues.permitStatusList,
        permitTierList: lookupValues.permitTierList,
      }));
      setFiltersInitialized(true);
    }
  }, [filtersInitialized, lookupValues, combinedData]);

  const debouncedSetLinkedWells = useDebounce((value) => {
    setFilters((prev) => ({ ...prev, linkedWells: value }));
  }, 200);

  const debouncedSetPermits = useDebounce((value) => {
    setFilters((prev) => ({ ...prev, permits: value }));
  }, 200);

  const debouncedSetPermitNames = useDebounce((value) => {
    setFilters((prev) => ({ ...prev, permitNames: value }));
  }, 200);

  const handleLinkedWellsChange = useCallback(
    (e) => {
      const { value } = e.target;
      setLinkedWellsDraft(value);
      debouncedSetLinkedWells(value);
    },
    [debouncedSetLinkedWells]
  );

  const handlePermitsChange = useCallback(
    (e) => {
      const { value } = e.target;
      setPermitsDraft(value);
      debouncedSetPermits(value);
    },
    [debouncedSetPermits]
  );

  const handlePermitNamesChange = useCallback(
    (e) => {
      const { value } = e.target;
      setPermitNamesDraft(value);
      debouncedSetPermitNames(value);
    },
    [debouncedSetPermitNames]
  );

  const displayedData = useMemo(() => {
    const rawData =
      filters.reportYearOption === "calendar" ? calendarData : fiscalData;
    if (!rawData.length) return [];

    // Don't show any data until filters are initialized
    if (!filtersInitialized) return [];

    const {
      startYear,
      endYear,
      aquiferGroupsList,
      mgmtZoneList,
      permitTypesList,
      permitUseList,
      permitStatusList,
      permitTierList,
      linkedWells,
      permits,
      permitNames,
    } = filters;

    const transformedData = rawData.map((row) => ({
      ...row,
      // Transform fields for display and searchability
      permit_group_name: transformTextField(row.permit_group_name),
      aquifer_groups_array: transformArrayField(row.aquifer_groups_array),
      management_zones: transformArrayField(row.management_zones),
      permit_types_array: transformArrayField(row.permit_types_array),
      permit_use_array: transformArrayField(row.permit_use_array),
      permit_status_array: transformArrayField(row.permit_status_array),
      permit_tier_name: transformTextField(row.permit_tier_name),
      assoc_permit_numbers: transformArrayField(row.assoc_permit_numbers),
      assoc_permit_names: transformArrayField(row.assoc_permit_names),
      linked_wells: transformArrayField(row.linked_wells),
    }));

    return transformedData.filter((row) => {
      const year =
        filters.reportYearOption === "calendar" ? row.c_year : row.fiscal_year;
      if (typeof year !== "number" || year < startYear || year > endYear) {
        return false;
      }

      const checkNull = (value, list) => {
        if (value === null) return list.includes("<blank>");
        return list.includes(value);
      };

      const checkArrayField = (value, list) => {
        if (value === null || value === undefined || value === "<empty>") {
          return list.includes("<empty>");
        }
        if (Array.isArray(value)) {
          if (value.length === 0 || (value.length === 1 && value[0] === null)) {
            return list.includes("<empty>");
          }
          return list.some((listItem) => {
            if (listItem === "<empty>") return false;
            return value.includes(listItem);
          });
        }
        // Handle comma-separated strings from transformArrayField
        if (typeof value === "string" && value.includes(", ")) {
          const parts = value.split(", ");
          return parts.some((part) => list.includes(part));
        }
        return list.includes(value);
      };

      if (!checkArrayField(row.aquifer_groups_array, aquiferGroupsList))
        return false;
      if (!checkArrayField(row.management_zones, mgmtZoneList)) return false;
      if (!checkArrayField(row.permit_types_array, permitTypesList))
        return false;
      if (!checkArrayField(row.permit_use_array, permitUseList)) return false;
      if (!checkArrayField(row.permit_status_array, permitStatusList))
        return false;
      if (!checkNull(row.permit_tier_name, permitTierList)) return false;

      // Text search filters
      if (
        linkedWells &&
        !row.linked_wells.toLowerCase().includes(linkedWells.toLowerCase())
      )
        return false;

      if (
        permits &&
        !row.assoc_permit_numbers.toLowerCase().includes(permits.toLowerCase())
      )
        return false;

      if (
        permitNames &&
        !row.assoc_permit_names
          .toLowerCase()
          .includes(permitNames.toLowerCase())
      )
        return false;

      return true;
    });
  }, [filters, calendarData, fiscalData, filtersInitialized]);

  // Generate table filter lookups from the transformed data
  // Only for fields that have select/dropdown filters in the UI
  const tableFilterLookups = useTableFilterLookups(displayedData, {
    aquifer_groups_array: { type: "array", field: "aquifer_groups_array" },
    management_zones: { type: "array", field: "management_zones" },
    permit_types_array: { type: "array", field: "permit_types_array" },
    permit_use_array: { type: "array", field: "permit_use_array" },
    permit_status_array: { type: "array", field: "permit_status_array" },
    permit_tier_name: { type: "text", field: "permit_tier_name" },
  });

  const columns = useMemo(() => {
    const yearCol = {
      title:
        filters.reportYearOption === "calendar"
          ? "Calendar Year"
          : "Fiscal Year",
      field: filters.reportYearOption === "calendar" ? "c_year" : "fiscal_year",
      cellStyle: { whiteSpace: "nowrap" },
    };

    const orderedMonths = getOrderedMonths(filters.reportYearOption);

    const monthCols = orderedMonths.map((month) => ({
      title: month,
      field: `${month.toLowerCase().substring(0, 3)}_gallons`,
      render: (row) =>
        formatNumber(row[`${month.toLowerCase().substring(0, 3)}_gallons`]),
      cellStyle: { whiteSpace: "nowrap" },
      filtering: false,
    }));

    const additionalCols = [
      {
        title: "Total",
        field: "tot_gallons",
        render: (row) => formatNumber(row.tot_gallons),
        cellStyle: { whiteSpace: "nowrap" },
        filtering: false,
      },
      {
        title: "Aquifer Groups",
        field: "aquifer_groups_array",
        cellStyle: { whiteSpace: "nowrap" },
        lookup: tableFilterLookups.aquifer_groups_array,
      },
      {
        title: "Management Zones",
        field: "management_zones",
        cellStyle: { whiteSpace: "nowrap" },
        lookup: tableFilterLookups.management_zones,
      },
      {
        title: "Permit Types",
        field: "permit_types_array",
        cellStyle: { whiteSpace: "nowrap" },
        lookup: tableFilterLookups.permit_types_array,
      },
      {
        title: "Permit Uses",
        field: "permit_use_array",
        cellStyle: { whiteSpace: "nowrap" },
        lookup: tableFilterLookups.permit_use_array,
      },
      {
        title: "Permits Status",
        field: "permit_status_array",
        cellStyle: { whiteSpace: "nowrap" },
        lookup: tableFilterLookups.permit_status_array,
      },
      {
        title: "Permit Tier",
        field: "permit_tier_name",
        cellStyle: { whiteSpace: "nowrap" },
        lookup: tableFilterLookups.permit_tier_name,
      },
      {
        title: "Permit Numbers",
        field: "assoc_permit_numbers",
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        title: "Permit Names",
        field: "assoc_permit_names",
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        title: "Linked Wells",
        field: "linked_wells",
        cellStyle: { whiteSpace: "nowrap" },
      },
    ];

    return [
      {
        title: "Permit Group",
        field: "permit_group_name",
        cellStyle: { whiteSpace: "nowrap" },
      },
      yearCol,
      ...monthCols,
      ...additionalCols,
    ];
  }, [filters, tableFilterLookups]);

  const updateFilter = useCallback((field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters({
      reportYearOption: "calendar",
      startYear: 2008,
      endYear: new Date().getFullYear(),
      aquiferGroupsList: lookupValues.aquiferGroupsList,
      mgmtZoneList: lookupValues.mgmtZoneList,
      permitTypesList: lookupValues.permitTypesList,
      permitUseList: lookupValues.permitUseList,
      permitStatusList: lookupValues.permitStatusList,
      permitTierList: lookupValues.permitTierList,
      linkedWells: "",
      permits: "",
      permitNames: "",
    });
    setLinkedWellsDraft("");
    setPermitsDraft("");
    setPermitNamesDraft("");
  }, [lookupValues]);

  if (hasError) return "An error has occurred. Please try again.";

  return (
    <>
      <Helmet title="Pumpage by Permit Group" />
      <Typography variant="h3" gutterBottom display="inline">
        Pumpage by Permit Group
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} exact to="/dashboard">
          Dashboard
        </Link>
        <Typography>Pumpage by Permit Group</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <Paper style={{ padding: "10px", marginBottom: "20px" }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Report Year Option</InputLabel>
              <Select
                label="Report Year Option"
                value={filters.reportYearOption}
                onChange={(e) =>
                  updateFilter("reportYearOption", e.target.value)
                }
              >
                <MenuItem value="calendar">Calendar Year</MenuItem>
                <MenuItem value="fiscal">Fiscal Year</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="From Year"
              type="number"
              variant="outlined"
              fullWidth
              value={filters.startYear}
              onChange={(e) =>
                updateFilter("startYear", Number(e.target.value))
              }
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="To Year"
              type="number"
              variant="outlined"
              fullWidth
              value={filters.endYear}
              onChange={(e) => updateFilter("endYear", Number(e.target.value))}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <MultiSelect
              label="Aquifer Group"
              options={lookupValues.aquiferGroupsList}
              selected={filters.aquiferGroupsList}
              onChange={(value) => updateFilter("aquiferGroupsList", value)}
              getLabel={(item) => item}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <MultiSelect
              label="Management Zone"
              options={lookupValues.mgmtZoneList}
              selected={filters.mgmtZoneList}
              onChange={(value) => updateFilter("mgmtZoneList", value)}
              getLabel={(item) => item}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <MultiSelect
              label="Permit Type"
              options={lookupValues.permitTypesList}
              selected={filters.permitTypesList}
              onChange={(value) => updateFilter("permitTypesList", value)}
              getLabel={(item) => item}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <MultiSelect
              label="Permit Use"
              options={lookupValues.permitUseList}
              selected={filters.permitUseList}
              onChange={(value) => updateFilter("permitUseList", value)}
              getLabel={(item) => item}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <MultiSelect
              label="Permit Status"
              options={lookupValues.permitStatusList}
              selected={filters.permitStatusList}
              onChange={(value) => updateFilter("permitStatusList", value)}
              getLabel={(item) => item}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <MultiSelect
              label="Permit Tier"
              options={lookupValues.permitTierList}
              selected={filters.permitTierList}
              onChange={(value) => updateFilter("permitTierList", value)}
              getLabel={(item) => item}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              variant="outlined"
              label="Linked Wells"
              fullWidth
              value={linkedWellsDraft}
              onChange={handleLinkedWellsChange}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              variant="outlined"
              label="Permits"
              fullWidth
              value={permitsDraft}
              onChange={handlePermitsChange}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              variant="outlined"
              label="Permit Names"
              fullWidth
              value={permitNamesDraft}
              onChange={handlePermitNamesChange}
            />
          </Grid>
        </Grid>

        <Box my={2} textAlign="right">
          <Button variant="contained" onClick={handleResetFilters}>
            Reset Filters
          </Button>
        </Box>
      </Paper>

      <Paper style={{ padding: "10px" }}>
        {!displayedData?.length > 0 ? (
          <Alert severity="info">
            <Typography
              variant="h6"
              component="div"
              style={{ padding: "16px" }}
            >
              No records available for these search parameters. Please try
              again.
            </Typography>
          </Alert>
        ) : (
          <MaterialTable
            id="Pumpage by Permit Group Report"
            title="Pumpage by Permit Group Report"
            columns={columns}
            isLoading={isLoading}
            data={displayedData}
            options={tableOptions}
          />
        )}
      </Paper>
    </>
  );
}
